// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect } from 'react';

import { type ProjectId, type Spool, useEvolvePaginatedGet } from '@evolvemep/api-ts';

import { isNil, isNotNil } from 'helpers/isNotNil';

/**
 * Looks up the Autodesk Cloud model that a spool was published from.
 */
export const useSpoolAutodeskCloudModel = (
  projectId: ProjectId,
  /** The spool to check for a cloud model. If `null`, no lookup is performed. */
  spool: Spool['spool'] | null,
) => {
  const { reset, fetchNextPage, data, entireCount, loading } = useEvolvePaginatedGet('shop/spool', {
    perPage: 1,
  });

  useEffect(() => {
    reset();
    if (isNil(spool)) return;
    void fetchNextPage({
      params: {
        spools: [spool],
        projectId,
      },
    });
  }, [reset, fetchNextPage, spool, projectId]);

  return {
    urn: data[0]?.autodeskCloudModelId ?? null,
    /**
     * Whether the lookup has finished. `urn` is only meaningful once this is `true` — before then
     * it is indistinguishable from a spool that has no model.
     */
    resolved: isNil(spool) || (isNotNil(entireCount) && !loading),
  };
};
